import { yupResolver } from '@hookform/resolvers/yup';
import { useQueryClient } from '@tanstack/react-query';
import React, { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import * as yup from 'yup';

import FormControls from 'components/FormControls/FormControls';
import Panel from 'components/Panel/Panel';
import TextArea from 'components/TextArea/TextArea';
import useCreateApplicantUpload from 'hooks/useCreateApplicantUpload';
import useNotification from 'hooks/useNotification';
import { t } from 'locales/i18n';

enum FormFields {
  NAME = 'name',
  DESCRIPTION = 'description',
}

interface FormData {
  [FormFields.NAME]: string;
  [FormFields.DESCRIPTION]: string;
}

interface ApplicantUploadFormProps {
  onClose: () => void;
}

const ApplicantUploadForm = ({ onClose }: ApplicantUploadFormProps) => {
  const { showErrorNotification, showSuccessNotification } = useNotification();
  const { interviewUid } = useParams();
  const queryClient = useQueryClient();
  const { createApplicantUpload, isLoading } = useCreateApplicantUpload();

  const FormSchema = useMemo(
    () =>
      yup.object().shape({
        [FormFields.NAME]: yup
          .string()
          .required('Applicant upload name is required')
          .max(50, t('Applicant upload name cannot exceed 50 characters')),
        [FormFields.DESCRIPTION]: yup
          .string()
          .required('Applicant upload description is required')
          .max(
            200,
            t('Applicant upload description cannot exceed 200 characters')
          ),
      }),
    [FormFields.NAME, FormFields.DESCRIPTION]
  );

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isDirty },
  } = useForm<FormData>({
    resolver: yupResolver(FormSchema),
    mode: 'onSubmit',
    values: {
      [FormFields.NAME]: '',
      [FormFields.DESCRIPTION]: '',
    },
  });

  const onSubmit = (data: FormData) => {
    if (!interviewUid) return;

    createApplicantUpload(
      {
        interviewUid,
        documentName: data.name,
        documentDescription: data.description,
      },
      {
        onSuccess: () => {
          showSuccessNotification(
            t('Supplemental material added'),
            t('Applicant upload successfully added')
          );
          onClose();

          queryClient.invalidateQueries({
            queryKey: ['getSupplementalMaterials', interviewUid],
          });

          queryClient.invalidateQueries({
            queryKey: ['getAssessmentStepDetails', interviewUid],
          });
        },
        onError: (error) => showErrorNotification(t('Error'), error.message),
      }
    );
  };

  return (
    <Panel isFocused={isDirty}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <fieldset disabled={isLoading}>
          <TextArea
            label={t('Name')}
            id={`upload-${FormFields.NAME}`}
            value={watch(FormFields.NAME)}
            errors={errors[FormFields.NAME]}
            data-testid="upload-name"
            errorDataTestId="upload-nameError"
            {...register(FormFields.NAME)}
          />
          <TextArea
            label={t('Instructions')}
            id={`upload-${FormFields.DESCRIPTION}`}
            value={watch(FormFields.DESCRIPTION)}
            errors={errors[FormFields.DESCRIPTION]}
            data-testid="upload-instructions"
            errorDataTestId="upload-instructionsError"
            {...register(FormFields.DESCRIPTION)}
          />
          <FormControls isLoading={isLoading} onCancel={onClose} />
        </fieldset>
      </form>
    </Panel>
  );
};

export default ApplicantUploadForm;
